import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { ArrowLeft, ChevronDownIcon, Save } from "lucide-react";

import { PromoCode } from "@/lib/supabase";
import { promoService } from "@/lib/database";
import { toast } from "sonner";

export default function NewPromoCodePage() {
  const navigate = useNavigate();

  const [newPromo, setNewPromo] = useState<Partial<PromoCode>>({
    code: "",
    description: "",
    promo_type: "percentage",
    amount: 0,
    is_stackable: false,
    is_active: true,
    usage_limit_per_customer: 0,
    total_usage_limit: 0,
  });

  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [startTime, setStartTime] = useState<string>("00:00:00");
  const [endTime, setEndTime] = useState<string>("00:00:00");
  const [openStartDatePicker, setOpenStartDatePicker] = useState(false);
  const [openEndDatePicker, setOpenEndDatePicker] = useState(false);
  const [hasUsageLimits, setHasUsageLimits] = useState(false);
  const [_, setLoading] = useState<boolean>(false);

  const handleCreatePromo = async () => {
    try {
      setLoading(true);

      if (!startDate || !endDate) {
        throw new Error("Start and end dates are required for promo codes.");
      }

      const startDateTime = new Date(
        `${startDate.toISOString().split("T")[0]}T${startTime}`
      );
      const endDateTime = new Date(
        `${endDate.toISOString().split("T")[0]}T${endTime}`
      );

      const payload: any = {
        code: newPromo.code?.trim().toUpperCase(),
        description: newPromo.description || null,
        promo_type: newPromo.promo_type,
        amount: newPromo.amount,
        is_stackable: newPromo.is_stackable,
        is_active: newPromo.is_active,
        usage_limit_per_customer: hasUsageLimits
          ? newPromo.usage_limit_per_customer
          : null,
        total_usage_limit: hasUsageLimits ? newPromo.total_usage_limit : null,
        start_date: startDateTime.toISOString(),
        end_date: endDateTime.toISOString(),
      };

      await promoService.create(payload);
      navigate("/admin/discounts-promos");
    } catch (error) {
      console.error("Failed to create promo code:", error);

      toast.error(
        error instanceof Error
          ? error.message
          : "An unexpected error occurred.",
        { position: "top-center" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex space-x-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Create New Promo Code
            </h1>
            <p className="text-gray-600">
              Fill in the details to create a new promotional code.
            </p>
          </div>
        </div>
      </div>

      <div className="gap-6">
        <div className="space-y-2">
          <Card>
            <CardHeader>
              <CardTitle>Promo Code Information</CardTitle>
              <CardDescription>Basic promo details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="code">Code *</Label>
                <Input
                  id="code"
                  value={newPromo.code}
                  onChange={(e) =>
                    setNewPromo((prev: Partial<PromoCode>) => ({
                      ...prev,
                      code: e.target.value,
                    }))
                  }
                />
                <p>{newPromo.code?.toUpperCase()}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newPromo.description}
                  onChange={(e) =>
                    setNewPromo((prev: Partial<PromoCode>) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="type">Promo Type *</Label>
                  <Select
                    value={newPromo.promo_type}
                    onValueChange={(value: "percentage" | "fixed") =>
                      setNewPromo((prev: Partial<PromoCode>) => ({
                        ...prev,
                        promo_type: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="fixed">Fixed Amount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="amount">Amount *</Label>
                  <Input
                    type="number"
                    id="amount"
                    value={newPromo.amount || ""}
                    inputMode="decimal"
                    onChange={(e) =>
                      setNewPromo((prev: Partial<PromoCode>) => ({
                        ...prev,
                        amount: parseFloat(e.target.value) || undefined,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="space-y-3 mt-4">
                <div className="space-x-2 items-center flex">
                  <Switch
                    id="usage-limits"
                    checked={hasUsageLimits}
                    onCheckedChange={(checked: boolean) => {
                      setHasUsageLimits(checked);
                      if (!checked) {
                        setNewPromo((prev: Partial<PromoCode>) => ({
                          ...prev,
                          usage_limit_per_customer: 0,
                          total_usage_limit: 0,
                        }));
                      }
                    }}
                  />
                  <Label htmlFor="usage-limits">Usage Limits</Label>
                </div>

                {hasUsageLimits && (
                  <div className="flex gap-4 flex-wrap justify-between">
                    <div className="flex flex-col gap-3">
                      <Label
                        htmlFor="usage-limit-per-customer"
                        className="px-1"
                      >
                        Usage Limit Per Customer
                      </Label>
                      <Input
                        type="number"
                        id="usage-limit-per-customer"
                        value={newPromo.usage_limit_per_customer || ""}
                        inputMode="numeric"
                        className="w-20"
                        onChange={(e) =>
                          setNewPromo((prev: Partial<PromoCode>) => ({
                            ...prev,
                            usage_limit_per_customer:
                              parseFloat(e.target.value) || 0,
                          }))
                        }
                      />
                    </div>

                    <div className="flex flex-col gap-3 sm:items-end">
                      <Label htmlFor="total-usage-limit" className="px-1">
                        Total Usage Cap
                      </Label>
                      <Input
                        type="number"
                        id="total-usage-limit"
                        value={newPromo.total_usage_limit || ""}
                        inputMode="numeric"
                        className="w-20"
                        onChange={(e) =>
                          setNewPromo((prev: Partial<PromoCode>) => ({
                            ...prev,
                            total_usage_limit: parseFloat(e.target.value) || 0,
                          }))
                        }
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4 mt-4">
                <Label>Validity Period *</Label>
                <div className="flex flex-col gap-4 md:flex-row flex-wrap">
                  <div className="flex gap-4 justify-between">
                    <div className="flex flex-col gap-3">
                      <Label htmlFor="start-date-picker" className="px-1">
                        Start Date
                      </Label>
                      <Popover
                        open={openStartDatePicker}
                        onOpenChange={setOpenStartDatePicker}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            id="start-date-picker"
                            className="w-32 justify-between font-normal"
                          >
                            {startDate
                              ? startDate.toLocaleDateString()
                              : "Select date"}
                            <ChevronDownIcon />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-auto overflow-hidden p-0"
                          align="start"
                        >
                          <Calendar
                            mode="single"
                            selected={startDate}
                            captionLayout="dropdown"
                            onSelect={(date) => {
                              setStartDate(date);
                              setOpenStartDatePicker(false);
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="flex flex-col gap-3">
                      <Label htmlFor="start-time-picker" className="px-1">
                        Start Time
                      </Label>
                      <Input
                        type="time"
                        id="start-time-picker"
                        step="1"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 justify-between">
                    <div className="flex flex-col gap-3">
                      <Label htmlFor="end-date-picker" className="px-1">
                        End Date
                      </Label>
                      <Popover
                        open={openEndDatePicker}
                        onOpenChange={setOpenEndDatePicker}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            id="end-date-picker"
                            className="w-32 justify-between font-normal"
                          >
                            {endDate
                              ? endDate.toLocaleDateString()
                              : "Select date"}
                            <ChevronDownIcon />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-auto overflow-hidden p-0"
                          align="start"
                        >
                          <Calendar
                            mode="single"
                            selected={endDate}
                            captionLayout="dropdown"
                            onSelect={(date) => {
                              setEndDate(date);
                              setOpenEndDatePicker(false);
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="flex flex-col gap-3">
                      <Label htmlFor="end-time-picker" className="px-1">
                        End Time
                      </Label>
                      <Input
                        type="time"
                        id="end-time-picker"
                        step="1"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-x-2 items-center flex">
                <Switch
                  id="stackable"
                  checked={newPromo.is_stackable}
                  onCheckedChange={(checked) =>
                    setNewPromo((prev: Partial<PromoCode>) => ({
                      ...prev,
                      is_stackable: checked,
                    }))
                  }
                />
                <div className="flex flex-col">
                  <Label htmlFor="stackable">Stackable</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow this promo to be used with other discounts or promo
                    codes
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Button
        onClick={handleCreatePromo}
        className="bg-[#254164] hover:bg-[#1a2d47] w-full"
      >
        <Save className="h-4 w-4 mr-2" />
        Create Promo Code
      </Button>
    </div>
  );
}
